import type { vec3 } from "gl-matrix";
import { useEffect } from "react";

import { useAppDispatch, useAppSelector } from "app/redux-store-interactions";
import { useExplorerGlobals } from "contexts/explorerGlobals";
import { selectIsProcoreMobileDesign } from "slices/explorer";
import { telemetry } from "telemetry";

import { markMarkerCreated } from "./markerCreationSuppression";
import { useStopMarkerCreation } from "./markerCreationUtils";
import {
    markersActions,
    selectAllCollections,
    selectCreationCollectionId,
    selectIsCreatingMarker,
} from "./markersSlice";
import { useMarkerDragHoverTracking } from "./useMarkerDragHoverTracking";

/**
 * Determines if mouse movement constitutes a drag gesture
 * @param start - Starting position from mousedown
 * @param end - Current/ending position
 * @param threshold - Distance threshold in pixels (default: 5)
 * @returns true if distance exceeds threshold
 */
function isDragGesture(start: { x: number; y: number }, end: { x: number; y: number }, threshold: number): boolean {
    const dx = end.x - start.x;
    const dy = end.y - start.y;
    const distance = Math.hypot(dx, dy);
    return distance > threshold;
}

export function MarkerCreationMode() {
    const dispatch = useAppDispatch();
    const isCreatingMarker = useAppSelector(selectIsCreatingMarker);
    const creationCollectionId = useAppSelector(selectCreationCollectionId);
    const collections = useAppSelector(selectAllCollections);
    const isMobileDesign = useAppSelector(selectIsProcoreMobileDesign);
    const stopMarkerCreation = useStopMarkerCreation();
    const {
        state: { view },
    } = useExplorerGlobals();

    const isSurfaceHighlightEnabled = collections[creationCollectionId ?? ""]?.config.enableSurfaceHighlight !== false;
    // Use hover tracking utility for surface highlighting during marker creation
    const { updateHoverObject, clearHoverObject } = useMarkerDragHoverTracking(view, {
        enabled: isSurfaceHighlightEnabled,
    });

    useEffect(() => {
        if (!isCreatingMarker || !creationCollectionId || !view) {
            return;
        }

        // Helper to get fresh config from Redux state (avoids stale closures)
        const getCreationConfig = () => collections[creationCollectionId]?.markerCreationConfig || {};

        const canvas = view.canvas;
        if (!canvas) {
            return;
        }

        let pointerDownPos: { x: number; y: number } | null = null;
        let isDragging = false;
        const DRAG_THRESHOLD = 5; // pixels - movement beyond this is considered a drag, not a click
        let isPickingPreview = false;

        const handleMove = async (e: PointerEvent) => {
            if (e.pointerType === "touch") {
                return;
            }

            // In mobile UI, PinOffsetCursor owns the preview — mouse events must not interfere.
            if (isMobileDesign) {
                return;
            }

            // Calculate canvas-relative position once at the start
            const rect = canvas.getBoundingClientRect();
            const currentPos = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };

            // Check if we're starting to drag
            if (pointerDownPos && !isDragging) {
                // If moved beyond threshold, we're dragging - clear preview
                if (isDragGesture(pointerDownPos, currentPos, DRAG_THRESHOLD)) {
                    isDragging = true;
                    dispatch(markersActions.updateCreationPreview(null));
                }
            }

            // Don't update preview if we're dragging
            if (isDragging) {
                return;
            }

            // Prevent multiple simultaneous picks
            if (isPickingPreview) {
                return;
            }
            isPickingPreview = true;

            try {
                const pickResult = await view.pick(currentPos.x, currentPos.y);

                if (pickResult?.position) {
                    const position: vec3 = pickResult.position;

                    // Update hover object for surface highlighting during creation
                    updateHoverObject(pickResult.objectId);

                    // Update preview icon position
                    dispatch(markersActions.updateCreationPreview(position));
                } else {
                    // Clear hover if no valid pick
                    updateHoverObject(undefined);
                }
            } catch (error) {
                // Swallow errors from pick to avoid unhandled rejections while ensuring cleanup
                // eslint-disable-next-line no-console
                console.error("Error during marker preview pick:", error);

                // Clear hover on error
                updateHoverObject(undefined);
            } finally {
                isPickingPreview = false;
            }
        };

        // Click detection - distinguish between click and drag (for camera rotation)

        const handlePointerDown = (e: PointerEvent) => {
            if (e.pointerType === "touch" || isMobileDesign) {
                return;
            }
            const rect = canvas.getBoundingClientRect();
            pointerDownPos = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };
            isDragging = false;
        };

        const handlePointerUp = async (e: PointerEvent) => {
            if (e.pointerType === "touch" || isMobileDesign) {
                return;
            }
            if (!pointerDownPos) {
                return;
            }

            const rect = canvas.getBoundingClientRect();
            const pointerUpPos = {
                x: e.clientX - rect.left,
                y: e.clientY - rect.top,
            };

            // Check if this was a drag
            if (isDragging) {
                pointerDownPos = null;
                isDragging = false;
                return;
            }

            // Reset state
            pointerDownPos = null;
            isDragging = false;

            // This was a click - create marker
            try {
                const pickResult = await view.pick(pointerUpPos.x, pointerUpPos.y);

                if (pickResult?.position) {
                    // Get fresh config from registry (avoids stale closures)
                    const creationConfig = getCreationConfig();

                    // Uses a plain array for Redux serializability
                    const position: [number, number, number] = [
                        pickResult.position[0],
                        pickResult.position[1],
                        pickResult.position[2],
                    ];

                    // Generate marker ID
                    const generateId = creationConfig?.generateId ?? (() => globalThis.crypto.randomUUID());
                    const markerId = generateId();

                    // Create marker using existing addMarker action
                    dispatch(
                        markersActions.addMarker({
                            collectionId: creationCollectionId,
                            marker: {
                                id: markerId,
                                position: position,
                                collectionId: creationCollectionId,
                                metadata: {},
                            },
                        }),
                    );

                    markMarkerCreated();

                    // Fire onCreated callback
                    creationConfig?.onCreated?.({
                        id: markerId,
                        position: position,
                    });
                }
            } catch (error) {
                telemetry.trackCaughtException({
                    source: "markers.MarkerCreationMode.create",
                    category: "render",
                    error,
                    severity: "error",
                });
                const err = error instanceof Error ? error : new Error(String(error));
                // Get fresh config for error callback too
                const creationConfig = getCreationConfig();
                creationConfig?.onError?.(err);
            }
        };

        // ESC key handler - stop creation mode
        const handleKeyDown = (e: KeyboardEvent) => {
            if (e.key === "Escape") {
                stopMarkerCreation();
            }
        };

        canvas.addEventListener("pointermove", handleMove);
        canvas.addEventListener("pointerdown", handlePointerDown);
        canvas.addEventListener("pointerup", handlePointerUp);
        globalThis.addEventListener("keydown", handleKeyDown);

        return () => {
            // Clean up hover object highlighting
            clearHoverObject();

            canvas.removeEventListener("pointermove", handleMove);
            canvas.removeEventListener("pointerdown", handlePointerDown);
            canvas.removeEventListener("pointerup", handlePointerUp);
            globalThis.removeEventListener("keydown", handleKeyDown);
        };
    }, [
        isCreatingMarker,
        creationCollectionId,
        isMobileDesign,
        view,
        dispatch,
        collections,
        updateHoverObject,
        clearHoverObject,
        stopMarkerCreation,
    ]);

    return null; // Headless component
}
